import TextField from "@mui/material/TextField";
import { EnforcementAppealsFilter } from "@shared/publicportal/contracts/EnforcementAppealsFilter";
import { EnforcementNoticeFilter } from "@shared/publicportal/contracts/EnforcementNoticeFilter";
import { SavedSearchType } from "@shared/publicportal/contracts/ISavedSearch";
import { PlanningAppealsFilter } from "@shared/publicportal/contracts/PlanningAppealsFilter";
import { PlanningApplicationsFilter } from "@shared/publicportal/contracts/PlanningApplicationsFilter";
import { PropertySearchFilter } from "@shared/publicportal/contracts/PropertySearchFilter";
import { ValidationResult } from "@shared/mirage/validators/IsStringNotEmptyValidator";
import { BuildingRegulationsFilter } from "@shared/publicportal/contracts/BuildingRegulationsFilter";
import IconButton from "@mui/material/IconButton";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

export const UserCollectedDataHelper = {
    mapLabel: (raw: string, searchType: SavedSearchType) => {
        const mapper: FieldsMapper = {
            PlanningApplications: {
                appRef: "Reference",
                appType: "Application Type",
                registeredDateFrom: "Registered From",
                registeredDateTo: "Registered To",
                committeeDateFrom: "Committee From",
                committeeDateTo: "Committee To",
                decisionDateFrom: "Decision From",
                decisionDateTo: "Decision To",
                receivedDateFrom: "Received From",
                receivedDateTo: "Received To",
                validatedDateFrom: "Validated From",
                validatedDateTo: "Validated To",
            },
            PlanningAppeals: {
                appealRef: "Appeal Reference",
                applicationRef: "Application Reference",
                planningInspectorateRef: "Planning Inspectorate Reference",
                appealType: "Appeal Type",
                decisionType: "Decision Type",
                appealAddress: "Appeal Address",
                appealLodgedDateFrom: "Lodged Date From",
                appealLodgedDateTo: "Lodged Date To",
                appealCreatedDateFrom: "Created From",
                appealCreatedDateTo: "Created To",
                appealDecisionDateFrom: "Decision From",
                appealDecisionDateTo: "Decision To",
            },
            EnforcementNotices: {
                noticeRef: "Notice Reference",
                complaintRef: "Complaint Reference",
                noticeStatus: "Status",
                noticeType: "Type",
                noticeWard: "Ward",
                noticeParish: "Parish",
                complianceDeadlineDateFrom: "Compliance Deadline From",
                complianceDeadlineDateTo: "Compliance Deadline To",
                noticeCreatedDateFrom: "Notice Created From",
                noticeCreatedDateTo: "Notice Created To",
                noticeAuthorisedDateFrom: "Notice Authorised From",
                noticeAuthorisedDateTo: "Notice Authorised To",
                noticeServedDateFrom: "Notice Served From",
                noticeServedDateTo: "Notice Served To",
            },
            EnforcementAppeals: {
                appealRef: "Appeal Reference",
                noticeRef: "Notice Reference",
                inspectorateRef: "Inspectorate Reference",
                appealType: "Appeal Type",
                ward: "Ward",
                parish: "Parish",
                appealLevel: "Appeal Level",
                decisionType: "Decision Type",
                decisionDateFrom: "Appeal Decision From",
                decisionDateTo: "Appeal Decision To",
                claimExpirationDateFrom: "Claim Expiration From",
                claimExpirationDateTo: "Claim Expiration To",
                dateStartedFrom: "Enforcement Started From",
                dateStartedTo: "Enforcement Started To",
                dateNotifiedFrom: "Enforcement Notified From",
                dateNotifiedTo: "Enforcement Notified To",
            },
            BuildingRegulations: {
                appRef: "Application Reference",
                status: "Status",
                applicant: "Applicant",
                agent: "Agent",
                partnershipType: "Partnership Type",
                appType: "Application Type",
                ward: "Ward",
                parish: "Parish",
                address: "Address",
                decisionDateFrom: "Decision Date From",
                decisionDateTo: "Decision Date To",
                receivedDateFrom: "Received Date From",
                receivedDateTo: "Received Date To",
                validationDateFrom: "Validation Date From",
                validationDateTo: "Validation Date To",
                dateStartedFrom: "Date Started From",
                dateStartedTo: "Date Started To",
                completedDateFrom: "Completed Date From",
                completedDateTo: "Completed Date To",
            },
            PropertySearch: {
                address: "Address",
                addressLine1: "Address Line 1",
                addressLine2: "Address Line 2",
                addressLine3: "Address Line 3",
                postTown: "Post Town",
                postCode: "Post Code",
                uprn: "UPRN",
                easting: "Easting",
                northing: "Northing",
            },
        };
        return mapper[searchType as SavedSearchType][raw as keyof CriteriaValues] || raw;
    },
    isGeometry: (key: string): boolean => {
        const keyLowerCase = key.toLowerCase();
        return keyLowerCase.indexOf("geometry") >= 0;
    },
    isDate: (value: string): boolean => {
        const dateMapper: ValuesMapper = {
            receivedDateFrom: true,
            receivedDateTo: true,
            registeredDateFrom: true,
            registeredDateTo: true,
            validationDateFrom: true,
            validationDateTo: true,
            appealLodgedDateFrom: true,
            appealLodgedDateTo: true,

            appealCreatedDateFrom: true,
            appealCreatedDateTo: true,
            appealDecisionDateFrom: true,
            appealDecisionDateTo: true,
            // enforcement
            complianceDeadlineDateFrom: true,
            complianceDeadlineDateTo: true,
            noticeCreatedDateFrom: true,
            noticeCreatedDateTo: true,
            noticeAuthorisedDateFrom: true,
            noticeAuthorisedDateTo: true,
            noticeServedDateFrom: true,
            noticeServedDateTo: true,
            // enforcement appeal
            decisionDateFrom: true,
            decisionDateTo: true,
            claimExpirationDateFrom: true,
            claimExpirationDateTo: true,
            dateStartedFrom: true,
            dateStartedTo: true,
            dateNotifiedFrom: true,
            dateNotifiedTo: true,
            //buildingRegulations
            committeeDateFrom: true,
            committeeDateTo: true,
            completedDateFrom: true,
            completedDateTo: true,
            validatedDateFrom: true,
            validatedDateTo: true,
        };

        return dateMapper[value as keyof ValuesMapper] || false;
    },
    clearEmptyValues: (object: CriteriaValues) => {
        Object.keys(object).forEach((key) => {
            const typedKey = key;
            const value: unknown = object[typedKey as keyof CriteriaValues];
            if (value == null || value === "") {
                delete object[key as keyof CriteriaValues];
            }
        });
    },
    getSearchName: (searchName: string, onChange: (value: string) => void, error: ValidationResult) => {
        return (
            <TextField
                variant="standard"
                className="saved-search-name"
                value={searchName || ""}
                onChange={(e) => onChange(e.target.value)}
                inputProps={{ className: "saved-search-name__input" }}
                error={!error.passed}
                helperText={!error.passed && error.error}
            />
        );
    },
};

type CriteriaValues = PlanningApplicationsFilter | PlanningAppealsFilter | EnforcementAppealsFilter | EnforcementNoticeFilter | PropertySearchFilter;
type FieldKeys =
    | keyof PlanningApplicationsFilter
    | keyof PlanningAppealsFilter
    | keyof EnforcementAppealsFilter
    | keyof EnforcementNoticeFilter
    | keyof BuildingRegulationsFilter
    | keyof PropertySearchFilter;
type FieldsMapper = {
    [key in SavedSearchType]: {
        [key in FieldKeys]?: string;
    };
};
type ValuesMapper = {
    [key in FieldKeys]?: boolean;
};

export default UserCollectedDataHelper;
export type { CriteriaValues };
